import { useEffect, useRef, useState } from "react";
import { useAuth } from "../../provider/AuthProvider.tsx";

const WARNING_THRESHOLDS_MIN = [60, 30, 15, 1];

const CHECK_INTERVAL_MS = 5000;
const AUTO_DISMISS_MS = 12000;

type Urgency = "info" | "warning" | "critical";

const getUrgency = (minutes: number): Urgency => {
  if (minutes <= 1) return "critical";
  if (minutes <= 15) return "warning";
  return "info";
};

const URGENCY_STYLES: Record<
  Urgency,
  { border: string; iconBg: string; iconColor: string }
> = {
  info: {
    border: "border-l-[#3C50E0]",
    iconBg: "bg-[#3C50E0]/10",
    iconColor: "text-[#3C50E0]",
  },
  warning: {
    border: "border-l-amber-500",
    iconBg: "bg-amber-500/10",
    iconColor: "text-amber-500",
  },
  critical: {
    border: "border-l-[#AA2808]",
    iconBg: "bg-[#AA2808]/10",
    iconColor: "text-[#AA2808]",
  },
};

const formatMinutesLabel = (minutes: number) =>
  minutes === 1 ? "1 минута" : `${minutes} минути`;

const SessionTimeoutMonitor = () => {
  const { jwt, sessionEnd } = useAuth();
  const alertedRef = useRef<Set<number>>(new Set());
  const dismissTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [activeWarning, setActiveWarning] = useState<number | null>(null);
  const [visible, setVisible] = useState(false); // drives the fade/slide transition

  // New token/session -> forget which thresholds we've already shown.
  useEffect(() => {
    alertedRef.current = new Set();
    setActiveWarning(null);
    setVisible(false);
  }, [sessionEnd]);

  useEffect(() => {
    if (!jwt || !sessionEnd) return;

    const checkSession = () => {
      const remainingSeconds = sessionEnd - Date.now() / 1000;
      if (remainingSeconds <= 0) return;

      let mostUrgentCrossed: number | null = null;

      for (const minutes of WARNING_THRESHOLDS_MIN) {
        const thresholdSeconds = minutes * 60;
        if (
          remainingSeconds <= thresholdSeconds &&
          !alertedRef.current.has(minutes)
        ) {
          alertedRef.current.add(minutes);
          mostUrgentCrossed = minutes;
        }
      }

      if (mostUrgentCrossed !== null) {
        if (dismissTimerRef.current) clearTimeout(dismissTimerRef.current);

        setActiveWarning(mostUrgentCrossed);
        requestAnimationFrame(() => setVisible(true));

        if (getUrgency(mostUrgentCrossed) !== "critical") {
          dismissTimerRef.current = setTimeout(
            () => setVisible(false),
            AUTO_DISMISS_MS
          );
        }
      }
    };

    checkSession();
    const intervalId = setInterval(checkSession, CHECK_INTERVAL_MS);

    return () => {
      clearInterval(intervalId);
      if (dismissTimerRef.current) clearTimeout(dismissTimerRef.current);
    };
  }, [jwt, sessionEnd]);

  const handleClose = () => {
    if (dismissTimerRef.current) clearTimeout(dismissTimerRef.current);
    setVisible(false);
  };

  const handleTransitionEnd = () => {
    if (!visible) setActiveWarning(null);
  };

  if (activeWarning === null) return null;

  const urgency = getUrgency(activeWarning);
  const styles = URGENCY_STYLES[urgency];

  return (
    <div
      className={`fixed right-4 top-24 z-[999999] w-full max-w-sm transform transition-all duration-300 ease-out ${
        visible ? "translate-y-0 opacity-100" : "-translate-y-3 opacity-0"
      }`}
      onTransitionEnd={handleTransitionEnd}
      role="alert"
    >
      <div
        className={`dark:bg-boxdark flex items-start gap-3 rounded-lg border-l-4 bg-white p-4 shadow-lg dark:shadow-none ${styles.border}`}
      >
        <span
          className={`mt-0.5 flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full ${styles.iconBg}`}
        >
          <svg
            className={`h-5 w-5 ${styles.iconColor}`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M12 9v3.75m0 3.75h.008v.008H12v-.008ZM21 12a9 9 0 11-18 0 9 9 0 0118 0Z"
            />
          </svg>
        </span>

        <div className="flex-1 pt-0.5">
          <p className="text-sm font-semibold text-black dark:text-white">
            Сесията Ви изтича след {formatMinutesLabel(activeWarning)}
          </p>
          <p className="text-body dark:text-bodydark mt-1 text-xs">
            Моля, запазете работата си, за да не загубите въведените данни.
          </p>
        </div>

        <button
          type="button"
          onClick={handleClose}
          aria-label="Затвори"
          className="text-body dark:text-bodydark flex-shrink-0 rounded p-0.5 hover:text-black dark:hover:text-white"
        >
          <svg
            className="h-4 w-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
      </div>
    </div>
  );
};

export default SessionTimeoutMonitor;
